import { create } from "zustand";
import type { ProfilePerformance, PublicUser, UserStats } from "@mk/shared";
import { api, post, setAccessToken, setRefreshHandler } from "./api.ts";
import { isNativeApp } from "./native.ts";
import { clearNativeRefreshToken, getNativeRefreshToken, saveNativeRefreshToken } from "./nativeSession.ts";
import { reconnectSocket } from "./socket.ts";

interface SessionResponse {
  accessToken: string;
  expiresIn: number;
  user: PublicUser;
  refreshToken?: string;
  usernameChangedAt?: number;
}

/** Hesabın hangi giriş yollarına sahip olduğu — silme onayı buna göre soruluyor. */
export interface AccountInfo {
  hasPassword: boolean;
  hasGoogle: boolean;
  hasApple: boolean;
}

interface MeResponse {
  user: PublicUser & { email: string; usernameChangedAt: number | null } & AccountInfo;
  stats: UserStats | null;
  performance: ProfilePerformance;
}

/** Google ya da Apple ile giriş: ya oturum açılır ya da kullanıcı adı seçilmesi istenir. */
export type IdentitySignInResult =
  | { status: "signed-in" }
  | { status: "needs-username"; ticket: string; email: string };

export type IdentityProvider = "google" | "apple";

/** Apple ile girişte native eklentinin döndürdüğü kimlik. */
export interface AppleCredential {
  identityToken: string;
  authorizationCode?: string;
}

type IdentityResponse =
  | ({ status: "signed-in" } & SessionResponse)
  | { status: "needs-username"; ticket: string; email: string };

export type SessionStatus = "loading" | "anon" | "authed";

interface SessionState {
  status: SessionStatus;
  user: PublicUser | null;
  account: AccountInfo | null;
  usernameChangedAt: number | null;
  stats: UserStats | null;
  performance: ProfilePerformance | null;
  bootstrap: () => Promise<void>;
  login: (login: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string) => Promise<void>;
  signInWithGoogle: (credential: string) => Promise<IdentitySignInResult>;
  signInWithApple: (credential: AppleCredential) => Promise<IdentitySignInResult>;
  completeSignUp: (provider: IdentityProvider, ticket: string, username: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (proof: { password?: string; credential?: string; appleIdentityToken?: string }) => Promise<void>;
  refreshStats: () => Promise<void>;
  renameUsername: (username: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

export const useSession = create<SessionState>((set, get) => ({
  status: "loading",
  user: null,
  account: null,
  usernameChangedAt: null,
  stats: null,
  performance: null,

  /** Sayfa açılışında: çerezdeki yenileme jetonuyla oturumu geri kurmayı dener. */
  async bootstrap() {
    try {
      const refreshToken = isNativeApp() ? await getNativeRefreshToken() : null;
      if (isNativeApp() && !refreshToken) throw new Error("no_native_session");
      const session = await post<SessionResponse>("/api/auth/refresh", isNativeApp() ? { refreshToken } : undefined);
      await saveNativeRefreshToken(session.refreshToken);
      setAccessToken(session.accessToken);
      set({ status: "authed", user: session.user });
      await get().refreshStats();
    } catch {
      setAccessToken(null);
      set({ status: "anon", user: null, account: null, stats: null, performance: null });
    }
  },

  async login(login, password) {
    const session = await post<SessionResponse>("/api/auth/login", { login, password });
    await saveNativeRefreshToken(session.refreshToken);
    setAccessToken(session.accessToken);
    set({ status: "authed", user: session.user });
    await get().refreshStats();
  },

  async register(email, username, password) {
    const session = await post<SessionResponse>("/api/auth/register", {
      email,
      username,
      password,
    });
    await saveNativeRefreshToken(session.refreshToken);
    setAccessToken(session.accessToken);
    set({ status: "authed", user: session.user });
    await get().refreshStats();
  },

  /**
   * Google'ın kimlik jetonuyla giriş. Hesap yoksa sunucu kullanıcı adı istiyor;
   * o durumda oturum açılmaz, çağıran adım adım devam eder.
   */
  async signInWithGoogle(credential) {
    return signInWithIdentity(await post<IdentityResponse>("/api/auth/google", { credential }));
  },

  /** Apple ile giriş (iOS uygulaması); Google'la aynı adımlar. */
  async signInWithApple(credential) {
    return signInWithIdentity(await post<IdentityResponse>("/api/auth/apple", credential));
  },

  async completeSignUp(provider, ticket, username) {
    const res = await post<{ status: "signed-in" } & SessionResponse>(`/api/auth/${provider}/complete`, {
      ticket,
      username,
    });
    await saveNativeRefreshToken(res.refreshToken);
    setAccessToken(res.accessToken);
    set({ status: "authed", user: res.user });
    await get().refreshStats();
  },

  async logout() {
    try {
      await post("/api/auth/logout");
    } finally {
      await clearNativeRefreshToken();
      setAccessToken(null);
      set({ status: "anon", user: null, account: null, stats: null, performance: null });
    }
  },

  /**
   * Hesabı kalıcı olarak siler. Şifre sunucuda doğrulanıyor; hata fırlatırsa
   * oturuma dokunulmaz, çağıran mesajı gösterir.
   */
  async deleteAccount(proof) {
    await api("/api/auth/me", { method: "DELETE", body: JSON.stringify(proof) });
    await clearNativeRefreshToken();
    setAccessToken(null);
    set({ status: "anon", user: null, account: null, stats: null, performance: null });
  },

  async refreshStats() {
    try {
      const me = await api<MeResponse>("/api/auth/me");
      set({
        stats: me.stats,
        performance: me.performance,
        user: { id: me.user.id, username: me.user.username, removeAds: me.user.removeAds },
        account: { hasPassword: me.user.hasPassword, hasGoogle: me.user.hasGoogle, hasApple: me.user.hasApple },
        usernameChangedAt: me.user.usernameChangedAt,
      });
    } catch {
      /* istatistik alınamazsa oturum yine de geçerli */
    }
  },

  async renameUsername(username) {
    const session = await api<SessionResponse>("/api/auth/me/username", {
      method: "PATCH", body: JSON.stringify({ username }),
    });
    await saveNativeRefreshToken(session.refreshToken);
    setAccessToken(session.accessToken);
    set((state) => ({ user: { ...state.user, ...session.user }, usernameChangedAt: session.usernameChangedAt ?? Date.now() }));
    reconnectSocket();
  },

  /** Diğer cihazlar düşer; bu cihaz sunucunun verdiği yeni oturumla devam eder. */
  async changePassword(currentPassword, newPassword) {
    const session = await api<SessionResponse>("/api/auth/me/password", {
      method: "PATCH", body: JSON.stringify({ currentPassword, newPassword }),
    });
    await saveNativeRefreshToken(session.refreshToken);
    setAccessToken(session.accessToken);
    reconnectSocket();
  },
}));

async function signInWithIdentity(res: IdentityResponse): Promise<IdentitySignInResult> {
  if (res.status === "needs-username") {
    return { status: "needs-username", ticket: res.ticket, email: res.email };
  }
  await saveNativeRefreshToken(res.refreshToken);
  setAccessToken(res.accessToken);
  useSession.setState({ status: "authed", user: res.user });
  await useSession.getState().refreshStats();
  return { status: "signed-in" };
}

/** 401 sonrası sessiz yenileme; başarısızsa oturumu düşürür. */
setRefreshHandler(async () => {
  try {
    const refreshToken = isNativeApp() ? await getNativeRefreshToken() : null;
    if (isNativeApp() && !refreshToken) throw new Error("no_native_session");
    const session = await post<SessionResponse>("/api/auth/refresh", isNativeApp() ? { refreshToken } : undefined);
    await saveNativeRefreshToken(session.refreshToken);
    setAccessToken(session.accessToken);
    useSession.setState({ status: "authed", user: session.user });
    return session.accessToken;
  } catch {
    await clearNativeRefreshToken();
    setAccessToken(null);
    useSession.setState({
      status: "anon",
      user: null,
      account: null,
      stats: null,
      performance: null,
    });
    return null;
  }
});
